import { RESPONSIBILITY } from '@trustero/trustero-api-web/lib/common/model_pb'
import palette from 'src/designSystem/variables/palette'
import { USER_ROLE } from '@trustero/trustero-api-web/lib/account/account_pb'
import {
  CONTROL_STATUS,
  Control,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { DocumentPrivacy } from '@trustero/trustero-api-web/lib/trust/trust_pb'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'

export type ControlDropdownProps = {
  control: Control.AsObject
  onClickConfirmationModal?: (onClick: () => void) => void
}

type DropDownOptionOld<T> = {
  key: T
  label: string
  colors: {
    text: string
    textHover: string
    background: string
    backgroundHover: string
  }
}

type DropDownOption<T> = {
  key: T
  label: string
  colors: {
    background: string
    border: string
  }
}

export type ControlStatusDropdownOption = DropDownOption<CONTROL_STATUS>

export type DocumentPrivacyDropdownOption = DropDownOptionOld<DocumentPrivacy>

export type ResponsibilityDropdownOption = DropDownOption<RESPONSIBILITY>

export enum UserRole {
  ADMIN = 'Admin',
  USER = 'User',
  READONLY = 'Read Only',
  AUDITOR = 'Auditor',
  TRUSTPORTAL_PUBLISHER = 'Trust Portal Publisher',
  TRUSTPORTAL_APPROVER = 'Trust Portal Approver',
}

export const UserRoleOptions: Record<USER_ROLE, string> = Object.freeze({
  [USER_ROLE.ADMIN]: UserRole.ADMIN,
  [USER_ROLE.USER]: UserRole.USER,
  [USER_ROLE.READONLY]: UserRole.READONLY,
  [USER_ROLE.AUDITOR]: UserRole.AUDITOR,
  [USER_ROLE.TRUSTPORTAL_PUBLISHER]: UserRole.TRUSTPORTAL_PUBLISHER,
  [USER_ROLE.TRUSTPORTAL_APPROVER]: UserRole.TRUSTPORTAL_APPROVER,
})

export const UserRoleDropdownOptions = [
  {
    key: USER_ROLE.ADMIN,
    label: UserRoleOptions[USER_ROLE.ADMIN],
    colors: {
      text: palette.neutral['900'],
      textHover: palette.neutral['900'],
      background: palette.neutral['100'],
      backgroundHover: palette.blue['400'],
    },
  },
  {
    key: USER_ROLE.USER,
    label: UserRoleOptions[USER_ROLE.USER],
    colors: {
      text: palette.neutral.white,
      textHover: palette.blue['50'],
      background: palette.neutral['50'],
      backgroundHover: palette.neutral['200'],
    },
  },
  {
    key: USER_ROLE.READONLY,
    label: UserRoleOptions[USER_ROLE.READONLY],
    colors: {
      text: palette.neutral.white,
      textHover: palette.neutral['900'],
      background: palette.neutral['50'],
      backgroundHover: palette.blue['200'],
    },
  },
  {
    key: USER_ROLE.AUDITOR,
    label: UserRoleOptions[USER_ROLE.AUDITOR],
    colors: {
      text: palette.neutral.white,
      textHover: palette.neutral['900'],
      background: palette.neutral['50'],
      backgroundHover: palette.blue['200'],
    },
  },
  {
    key: USER_ROLE.TRUSTPORTAL_PUBLISHER,
    label: UserRoleOptions[USER_ROLE.TRUSTPORTAL_PUBLISHER],
    colors: {
      text: palette.neutral.white,
      textHover: palette.neutral['50'],
      background: palette.neutral['50'],
      backgroundHover: palette.blue['200'],
    },
  },
  {
    key: USER_ROLE.TRUSTPORTAL_APPROVER,
    label: UserRoleOptions[USER_ROLE.TRUSTPORTAL_APPROVER],
    colors: {
      text: palette.neutral.white,
      textHover: palette.neutral['50'],
      background: palette.neutral['50'],
      backgroundHover: palette.blue['200'],
    },
  },
]

export enum Responsibility {
  DIRECT_RESPONSIBILITY = 'Direct Responsibility',
  OUTSOURCED_NO_ATTESTATION = 'Outsourced - No Attestation',
  OUTSOURCED_ATTESTATION = 'Outsourced - Attestation',
  INHERITED = 'Inherited',
  NOT_APPLICABLE = 'Not Applicable',
  TMP_OUTSOURCED = 'Outsourced',
}

export enum ControlStatus {
  NEEDSATTENTION = 'Needs Attention',
  NOTAPPLICABLE = 'Not Applicable',
  READYFORREVIEW = 'Ready for Review',
  ACCEPTED = 'Accepted',
  AUDITREADY = 'Audit Ready',
  FAILEDTEST = 'Failed Test',
}

export enum DocumentPrivacyStatus {
  PUBLIC = 'Public',
  SEMI_PRIVATE = 'Confidential',
  PRIVATE = 'Restricted',
}

export const ControlStatusOptions: Record<CONTROL_STATUS, string> =
  Object.freeze({
    [CONTROL_STATUS.NEEDSATTENTION]: ControlStatus.NEEDSATTENTION,
    [CONTROL_STATUS.NOTAPPLICABLE]: ControlStatus.NOTAPPLICABLE,
    [CONTROL_STATUS.READYFORREVIEW]: ControlStatus.READYFORREVIEW,
    [CONTROL_STATUS.ACCEPTED]: ControlStatus.ACCEPTED,
    [CONTROL_STATUS.AUDITREADY]: ControlStatus.AUDITREADY,
    [CONTROL_STATUS.FAILEDTEST]: ControlStatus.FAILEDTEST,
  })

// The custom status a control shows, when the control has one that still exists;
// otherwise the control shows its built-in status.
export const findCustomControlStatus = (
  customStatusId: string,
  customStatuses: ControlStatusItem[] | undefined,
): ControlStatusItem | undefined =>
  customStatusId
    ? customStatuses?.find(
        (status) => status.getId() === customStatusId && !!status.getName(),
      )
    : undefined

// The status name a control shows, custom or built-in.
export const getControlStatusName = (
  status: CONTROL_STATUS,
  customStatusId: string,
  customStatuses: ControlStatusItem[] | undefined,
): string =>
  findCustomControlStatus(customStatusId, customStatuses)?.getName() ||
  ControlStatusOptions[status]

export const DocumentPrivacyOptions: Record<DocumentPrivacy, string> =
  Object.freeze({
    [DocumentPrivacy.PUBLIC]: DocumentPrivacyStatus.PUBLIC,
    [DocumentPrivacy.SEMI_PRIVATE]: DocumentPrivacyStatus.SEMI_PRIVATE,
    [DocumentPrivacy.PRIVATE]: DocumentPrivacyStatus.PRIVATE,
  })

export const ResponsibilityOptions: Record<RESPONSIBILITY, string> =
  Object.freeze({
    [RESPONSIBILITY.DIRECT_RESPONSIBILITY]:
      Responsibility.DIRECT_RESPONSIBILITY,
    [RESPONSIBILITY.INHERITED]: Responsibility.INHERITED,
    [RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION]:
      Responsibility.OUTSOURCED_NO_ATTESTATION,
    [RESPONSIBILITY.OUTSOURCED_ATTESTATION]:
      Responsibility.OUTSOURCED_ATTESTATION,
    [RESPONSIBILITY.NOT_APPLICABLE]: Responsibility.NOT_APPLICABLE,
    [RESPONSIBILITY.TMP_OUTSOURCED]: Responsibility.TMP_OUTSOURCED,
  })

export const ResponsibilityCopy: Record<RESPONSIBILITY, string> = Object.freeze(
  {
    [RESPONSIBILITY.DIRECT_RESPONSIBILITY]: 'a direct responsibility',
    [RESPONSIBILITY.INHERITED]: 'an inherited responsibility',
    [RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION]:
      'outsourced to a vendor with no attestation',
    [RESPONSIBILITY.OUTSOURCED_ATTESTATION]:
      'outsourced to a vendor with an attestation',
    [RESPONSIBILITY.NOT_APPLICABLE]: 'not applicable',
    [RESPONSIBILITY.TMP_OUTSOURCED]: 'outsourced',
  },
)

export const ResponsibilityToOptions: Record<string, RESPONSIBILITY> =
  Object.freeze({
    [Responsibility.DIRECT_RESPONSIBILITY]:
      RESPONSIBILITY.DIRECT_RESPONSIBILITY,
    [Responsibility.OUTSOURCED_NO_ATTESTATION]:
      RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION,
    [Responsibility.OUTSOURCED_ATTESTATION]:
      RESPONSIBILITY.OUTSOURCED_ATTESTATION,
    [Responsibility.INHERITED]: RESPONSIBILITY.INHERITED,
    [Responsibility.NOT_APPLICABLE]: RESPONSIBILITY.NOT_APPLICABLE,
  })

export const ResponsibilityDropdownOptions: ResponsibilityDropdownOption[] = [
  {
    key: RESPONSIBILITY.DIRECT_RESPONSIBILITY,
    label: ResponsibilityOptions[RESPONSIBILITY.DIRECT_RESPONSIBILITY],
    colors: {
      background: palette.neutral['50'],
      border: palette.neutral['700'],
    },
  },
  {
    key: RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION,
    label: ResponsibilityOptions[RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION],
    colors: {
      background: palette.neutral['50'],
      border: palette.neutral['700'],
    },
  },
  {
    key: RESPONSIBILITY.OUTSOURCED_ATTESTATION,
    label: ResponsibilityOptions[RESPONSIBILITY.OUTSOURCED_ATTESTATION],
    colors: {
      background: palette.neutral['400'],
      border: palette.neutral['900'],
    },
  },
  {
    key: RESPONSIBILITY.INHERITED,
    label: ResponsibilityOptions[RESPONSIBILITY.INHERITED],
    colors: {
      background: palette.neutral['400'],
      border: palette.neutral['900'],
    },
  },
  {
    key: RESPONSIBILITY.NOT_APPLICABLE,
    label: ResponsibilityOptions[RESPONSIBILITY.NOT_APPLICABLE],
    colors: {
      background: palette.neutral['100'],
      border: palette.neutral['600'],
    },
  },
  {
    key: RESPONSIBILITY.TMP_OUTSOURCED,
    label: ResponsibilityOptions[RESPONSIBILITY.TMP_OUTSOURCED],
    colors: {
      background: palette.neutral['50'],
      border: palette.neutral['700'],
    },
  },
]

export const PolicyResponsibilityDropdownOptions =
  ResponsibilityDropdownOptions.filter(
    (option) =>
      option.key !== RESPONSIBILITY.OUTSOURCED_ATTESTATION &&
      option.key !== RESPONSIBILITY.OUTSOURCED_NO_ATTESTATION &&
      option.key !== RESPONSIBILITY.TMP_OUTSOURCED &&
      option.key !== RESPONSIBILITY.INHERITED,
  )

export const ControlStatusDropdownOptions: ControlStatusDropdownOption[] = [
  {
    key: CONTROL_STATUS.NEEDSATTENTION,
    label: ControlStatusOptions[CONTROL_STATUS.NEEDSATTENTION],
    colors: {
      background: palette.orange['50'],
      border: palette.orange['700'],
    },
  },
  {
    key: CONTROL_STATUS.READYFORREVIEW,
    label: ControlStatusOptions[CONTROL_STATUS.READYFORREVIEW],
    colors: {
      background: palette.neutral.white,
      border: palette.neutral['300'],
    },
  },
  {
    key: CONTROL_STATUS.ACCEPTED,
    label: ControlStatusOptions[CONTROL_STATUS.ACCEPTED],
    colors: {
      background: palette.green['50'],
      border: palette.green['500'],
    },
  },
  {
    key: CONTROL_STATUS.NOTAPPLICABLE,
    label: ControlStatusOptions[CONTROL_STATUS.NOTAPPLICABLE],
    colors: {
      background: palette.neutral['100'],
      border: palette.neutral['600'],
    },
  },
]

export const DocumentPrivacyDropdownOptions: DocumentPrivacyDropdownOption[] = [
  {
    key: DocumentPrivacy.PUBLIC,
    label: DocumentPrivacyOptions[DocumentPrivacy.PUBLIC],
    colors: {
      text: palette.neutral.white,
      background: palette.blue['500'],
      backgroundHover: palette.blue['50'],
      textHover: palette.neutral['900'],
    },
  },
  {
    key: DocumentPrivacy.SEMI_PRIVATE,
    label: DocumentPrivacyOptions[DocumentPrivacy.SEMI_PRIVATE],
    colors: {
      text: palette.neutral.white,
      background: palette.green['500'],
      backgroundHover: palette.blue['50'],
      textHover: palette.neutral['900'],
    },
  },
  {
    key: DocumentPrivacy.PRIVATE,
    label: DocumentPrivacyOptions[DocumentPrivacy.PRIVATE],
    colors: {
      text: palette.neutral.white,
      background: palette.neutral['400'],
      backgroundHover: palette.blue['50'],
      textHover: palette.neutral['900'],
    },
  },
]
